/**
 * GMA-1618 (CompassCare Web 3/14 — Vitals triage) — triage table.
 *
 * Rows are ordered by triage priority (worst deviation-from-baseline first, via
 * the shared vitalsModel). Each vital cell shows the value and — when it
 * deviates meaningfully from the client's personal baseline — highlights and
 * appends the "±N% vs baseline" delta. Stale rows (readings older than the
 * threshold) are visually distinct and labelled, so stale numbers are never
 * shown as current (AC). Selecting a client opens the detail panel (parent).
 *
 * Presentational + fail-safe. a11y: a real <table> with a caption + column
 * headers; the client cell is a <button> so rows are keyboard-operable without
 * an ambiguous clickable <tr>. Colors from the single design-token set;
 * `offline` status renders distinct from clinical severities.
 */
import React from "react";
import PropTypes from "prop-types";
import SeverityBadge from "./SeverityBadge";
import {
  sortByTriagePriority,
  deviationPct,
  formatBaselineDelta,
  isStale,
  OUT_OF_RANGE_PCT,
} from "../../pages/web/vitalsModel";
import { formatRelativeTime } from "../../utils/webFormat";
import { theme, severity } from "../../styles/designTokens";
import {
  classifyVitalReading,
  srOnlyStyle,
  vitalRangeColor,
  vitalSeverityA11yLabel,
  vitalSeverityMarker,
  worstVitalSeverity,
} from "../../utils/vitalSeverity";

const VITAL_COLUMNS = Object.freeze([
  { key: "hr", label: "HR" },
  { key: "spo2", label: "SpO₂" },
  { key: "rr", label: "RR" },
  { key: "temp", label: "Temp" },
]);

const th = {
  textAlign: "left",
  padding: "8px 10px",
  fontSize: 12,
  color: theme.textMuted,
  borderBottom: `1px solid ${theme.border}`,
  whiteSpace: "nowrap",
};
const td = { padding: "8px 10px", fontSize: 13, whiteSpace: "nowrap" };

function worstOfBp(bp, bands, bpSysPct, bpDiaPct) {
  const hasSys =
    typeof bp.systolic === "number" && Number.isFinite(bp.systolic) && bp.systolic > 0;
  const hasDia =
    typeof bp.diastolic === "number" &&
    Number.isFinite(bp.diastolic) &&
    bp.diastolic > 0;
  if (!hasSys && !hasDia) return null;
  return worstVitalSeverity(
    hasSys
      ? classifyVitalReading({
          value: bp.systolic,
          metricType: "blood_pressure_systolic",
          customThresholds: bands,
          deviationPct: bpSysPct,
        })
      : "unclassified",
    hasDia
      ? classifyVitalReading({
          value: bp.diastolic,
          metricType: "blood_pressure_diastolic",
          customThresholds: bands,
          deviationPct: bpDiaPct,
        })
      : "unclassified",
  );
}

function VitalCell({ value, baseline, metricKey, customThresholds }) {
  const pct = deviationPct(value, baseline);
  const outOfRange = pct !== null && Math.abs(pct) >= OUT_OF_RANGE_PCT;
  // react-app#374: zero/negative is an absence (device offline), not a
  // measurement — render it as absent, in the same grammar as missing data.
  const shown =
    typeof value === "number" && Number.isFinite(value) && value > 0
      ? value
      : "—";
  const level =
    shown === "—"
      ? null
      : classifyVitalReading({
          value,
          metricType: metricKey,
          customThresholds,
          deviationPct: pct,
        });
  const color = (level && vitalRangeColor(level)) || theme.textMuted;
  const marker = level ? vitalSeverityMarker(level) : "";
  const a11ySeverity = level ? vitalSeverityA11yLabel(level) : null;
  return (
    <td
      style={{
        ...td,
        color,
        fontFamily: theme.fontMono,
      }}
      data-out-of-range={outOfRange ? "true" : "false"}
      data-vital-severity={level || undefined}
    >
      {shown}
      {/* Severity is never color-only: visible marker + announced word. */}
      {marker ? (
        <span aria-hidden="true" style={{ marginLeft: 4, fontWeight: 700 }}>
          {marker}
        </span>
      ) : null}
      {a11ySeverity ? <span style={srOnlyStyle}>{a11ySeverity}</span> : null}
      {outOfRange ? (
        <span
          // The delta is an out-of-range signal against this person's OWN
          // baseline, independent of the absolute clinical band: coloring it
          // with the band color renders an out-of-range indicator green
          // whenever the reading is in-band (HR 72 vs a baseline of 55).
          style={{ marginLeft: 6, fontSize: 11, color: severity.atRisk.color }}
          data-testid="baseline-delta"
        >
          {formatBaselineDelta(value, baseline)}
        </span>
      ) : null}
    </td>
  );
}

VitalCell.propTypes = {
  value: PropTypes.number,
  baseline: PropTypes.number,
  metricKey: PropTypes.string,
  customThresholds: PropTypes.object,
};

export default function VitalsTable({ rows, now, staleMinutes = 15, onSelectClient }) {
  const ordered = sortByTriagePriority(rows);
  const selectable = typeof onSelectClient === "function";

  return (
    <table
      data-testid="vitals-table"
      style={{
        width: "100%",
        borderCollapse: "collapse",
        color: theme.textPrimary,
        fontFamily: theme.fontUI,
        background: theme.bg,
      }}
    >
      <caption
        style={{
          captionSide: "top",
          textAlign: "left",
          padding: "0 0 8px",
          color: theme.textMuted,
        }}
      >
        Vitals triage
      </caption>
      <thead>
        <tr>
          <th scope="col" style={th}>
            Client
          </th>
          {VITAL_COLUMNS.map((c) => (
            <th scope="col" style={th} key={c.key}>
              {c.label}
            </th>
          ))}
          <th scope="col" style={th}>
            BP
          </th>
          <th scope="col" style={th}>
            Updated
          </th>
          <th scope="col" style={th}>
            Status
          </th>
        </tr>
      </thead>
      <tbody>
        {ordered.length === 0 ? (
          <tr>
            <td
              data-testid="vitals-table-empty"
              colSpan={VITAL_COLUMNS.length + 4}
              style={{ ...td, color: theme.textMuted, padding: 16 }}
            >
              No vitals to triage
            </td>
          </tr>
        ) : (
          ordered.map((row, index) => {
            const v = row.vitals || {};
            const b = row.baselines || {};
            const stale = isStale(row.lastUpdate, now, staleMinutes);
            const name = row.clientName || "Unknown client";
            const offline = row.status === "offline";
            const bp = v.bp || {};
            const bpBase = b.bp || {};
            // Use ABSOLUTE deviation (a large drop is out of range too), and
            // surface the more-deviated of the two components so the BP cell can
            // show its "±N% vs baseline" delta like the other vitals.
            const bpSysPct = deviationPct(bp.systolic, bpBase.systolic);
            const bpDiaPct = deviationPct(bp.diastolic, bpBase.diastolic);
            const bpWorst =
              Math.abs(bpDiaPct ?? 0) > Math.abs(bpSysPct ?? 0)
                ? { value: bp.diastolic, baseline: bpBase.diastolic, pct: bpDiaPct }
                : { value: bp.systolic, baseline: bpBase.systolic, pct: bpSysPct };
            const bpOut =
              bpWorst.pct !== null && Math.abs(bpWorst.pct) >= OUT_OF_RANGE_PCT;
            const bands = row.customThresholds || row.vitalsThresholds || undefined;
            // Number.isFinite, not typeof: Infinity/NaN are numbers and
            // Infinity passes a > 0 check, so a malformed half printed as
            // "Infinity/80" and the pair took its color from the surviving
            // normal half. Matches the per-vital cell validation.
            const bpShown =
              Number.isFinite(bp.systolic) &&
              Number.isFinite(bp.diastolic) &&
              bp.systolic > 0 &&
              bp.diastolic > 0;
            // No severity on an absent pair — a "—" cell must not carry a
            // clinical color or data attribute from a half-present reading.
            const bpLevel = bpShown ? worstOfBp(bp, bands, bpSysPct, bpDiaPct) : null;
            const bpColor = (bpLevel && vitalRangeColor(bpLevel)) || theme.textMuted;
            const bpMarker = bpLevel ? vitalSeverityMarker(bpLevel) : "";
            const bpA11y = bpLevel ? vitalSeverityA11yLabel(bpLevel) : null;
            return (
              <tr
                key={row.id ?? `${name}-${index}`}
                data-testid="vitals-row"
                data-stale={stale ? "true" : "false"}
                style={{
                  borderBottom: `1px solid ${theme.border}`,
                  background: stale ? theme.surface : "transparent",
                  opacity: stale ? 0.7 : 1,
                }}
              >
                <th scope="row" style={{ ...td, fontWeight: "normal" }}>
                  <button
                    type="button"
                    data-testid="vitals-open"
                    disabled={!selectable}
                    aria-label={`Open vitals for ${name}`}
                    onClick={selectable ? () => onSelectClient(row) : undefined}
                    style={{
                      background: "none",
                      border: "none",
                      color: theme.textPrimary,
                      cursor: selectable ? "pointer" : "default",
                      fontFamily: theme.fontUI,
                      fontSize: 13,
                      padding: 0,
                      textAlign: "left",
                    }}
                  >
                    {name}
                  </button>
                </th>
                {VITAL_COLUMNS.map((c) => (
                  <VitalCell
                    key={c.key}
                    value={v[c.key]}
                    baseline={b[c.key]}
                    metricKey={c.key}
                    customThresholds={bands}
                  />
                ))}
                <td
                  style={{
                    ...td,
                    fontFamily: theme.fontMono,
                    color: bpColor,
                  }}
                  data-out-of-range={bpOut ? "true" : "false"}
                  data-vital-severity={bpLevel || undefined}
                >
                  {bpShown ? `${bp.systolic}/${bp.diastolic}` : "—"}
                  {bpMarker ? (
                    <span aria-hidden="true" style={{ marginLeft: 4, fontWeight: 700 }}>
                      {bpMarker}
                    </span>
                  ) : null}
                  {bpA11y ? <span style={srOnlyStyle}>{bpA11y}</span> : null}
                  {bpOut ? (
                    <span
                      data-testid="bp-baseline-delta"
                      // Same as the per-vital delta above: at-risk accent,
                      // independent of the BP value's clinical band.
                      style={{ marginLeft: 6, fontSize: 11, color: severity.atRisk.color }}
                    >
                      {formatBaselineDelta(bpWorst.value, bpWorst.baseline)}
                    </span>
                  ) : null}
                </td>
                <td style={{ ...td, color: theme.textMuted, fontFamily: theme.fontMono }}>
                  {formatRelativeTime(row.lastUpdate, now)}
                  {stale ? (
                    <span
                      data-testid="vitals-stale"
                      style={{ marginLeft: 6, fontSize: 11, color: severity.caution.color }}
                    >
                      STALE
                    </span>
                  ) : null}
                </td>
                <td style={td}>
                  {offline ? (
                    <span style={{ fontSize: 12, color: theme.textMuted }}>
                      Offline
                    </span>
                  ) : (
                    <SeverityBadge level={row.status} showDot={false} />
                  )}
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}

VitalsTable.propTypes = {
  /** Triage rows: { id, clientName, status, vitals, baselines, lastUpdate }. */
  rows: PropTypes.array,
  /** Reference time for elapsed/staleness; defaults downstream to Date.now(). */
  now: PropTypes.number,
  /** Minutes after which a reading is stale. */
  staleMinutes: PropTypes.number,
  /** Called with the row when its client is opened. */
  onSelectClient: PropTypes.func,
};
